import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { EVENT_COLORS, RECURRENCE_PRESETS } from "@/types/calendar";
import type { CalendarEventRow } from "@/types/calendar";

const eventSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  location: z.string().optional(),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  isAllDay: z.boolean(),
  color: z.string().optional(),
  recurrenceRule: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

interface EventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  defaultValues?: {
    dbEvent?: CalendarEventRow;
    startTime?: string;
    endTime?: string;
  };
  onDelete?: () => void;
}

function toDatetimeLocal(isoString: string): string {
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function fromDatetimeLocal(datetimeLocal: string): string {
  return new Date(datetimeLocal).toISOString();
}

export function EventFormDialog({
  open,
  onOpenChange,
  mode,
  defaultValues,
  onDelete,
}: EventFormDialogProps) {
  const utils = trpc.useUtils();
  const dbEvent = defaultValues?.dbEvent;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<EventFormData>({
    defaultValues: {
      title: "",
      description: "",
      location: "",
      startTime: "",
      endTime: "",
      isAllDay: false,
      color: EVENT_COLORS[0].value,
      recurrenceRule: "",
    },
  });

  React.useEffect(() => {
    if (open) {
      if (dbEvent) {
        reset({
          title: dbEvent.title,
          description: dbEvent.description ?? "",
          location: dbEvent.location ?? "",
          startTime: toDatetimeLocal(dbEvent.start_time),
          endTime: toDatetimeLocal(dbEvent.end_time),
          isAllDay: dbEvent.is_all_day,
          color: dbEvent.color ?? EVENT_COLORS[0].value,
          recurrenceRule: dbEvent.recurrence_rule ?? "",
        });
      } else {
        reset({
          title: "",
          description: "",
          location: "",
          startTime: defaultValues?.startTime ? toDatetimeLocal(defaultValues.startTime) : "",
          endTime: defaultValues?.endTime ? toDatetimeLocal(defaultValues.endTime) : "",
          isAllDay: false,
          color: EVENT_COLORS[0].value,
          recurrenceRule: "",
        });
      }
    }
  }, [open, dbEvent, defaultValues, reset]);

  const createMutation = trpc.calendar.create.useMutation({
    onSuccess: () => {
      utils.calendar.listByRange.invalidate();
      onOpenChange(false);
    },
  });

  const updateMutation = trpc.calendar.update.useMutation({
    onSuccess: () => {
      utils.calendar.listByRange.invalidate();
      onOpenChange(false);
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: EventFormData) => {
    const payload = {
      title: data.title,
      description: data.description || undefined,
      location: data.location || undefined,
      startTime: fromDatetimeLocal(data.startTime),
      endTime: fromDatetimeLocal(data.endTime),
      isAllDay: data.isAllDay,
      color: data.color || undefined,
      recurrenceRule: data.recurrenceRule || undefined,
    };

    if (mode === "create") {
      createMutation.mutate(payload);
    } else if (dbEvent) {
      updateMutation.mutate({ id: dbEvent.id, ...payload });
    }
  };

  const color = watch("color");
  const isAllDay = watch("isAllDay");
  const recurrenceRule = watch("recurrenceRule");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New Event" : "Edit Event"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Event title"
              {...register("title", { required: "Title is required" })}
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Description (optional)"
              rows={2}
              {...register("description")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input id="location" placeholder="Location (optional)" {...register("location")} />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="isAllDay"
              checked={isAllDay}
              onCheckedChange={(checked: boolean) => setValue("isAllDay", checked)}
            />
            <Label htmlFor="isAllDay">All day</Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start</Label>
              <Input
                id="startTime"
                type={isAllDay ? "date" : "datetime-local"}
                {...register("startTime", {
                  required: "Start time is required",
                })}
              />
              {errors.startTime && (
                <p className="text-xs text-destructive">{errors.startTime.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">End</Label>
              <Input
                id="endTime"
                type={isAllDay ? "date" : "datetime-local"}
                {...register("endTime", { required: "End time is required" })}
              />
              {errors.endTime && (
                <p className="text-xs text-destructive">{errors.endTime.message}</p>
              )}
            </div>
          </div>

          {/* Color picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {EVENT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setValue("color", c.value)}
                  className={`h-7 w-7 rounded-full border-2 transition-all ${
                    color === c.value ? "scale-110 border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          {/* Recurrence */}
          <div className="space-y-2">
            <Label>Repeat</Label>
            <Select
              value={recurrenceRule}
              onValueChange={(value: string) => setValue("recurrenceRule", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="No repeat" />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_PRESETS.map((preset) => (
                  <SelectItem key={preset.label} value={preset.value || "none"}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            {mode === "edit" && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}
                disabled={isSubmitting}
                className="mr-auto"
              >
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? mode === "create"
                  ? "Creating..."
                  : "Saving..."
                : mode === "create"
                  ? "Create"
                  : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
